// @gs/feedback/client — FeedbackModal : creation de ticket (type/titre/description
// + PJ + capture diagnostics auto : userAgent, viewport, url, locale). DS V2.
import { useRef, useState } from 'react';
import { Paperclip, X } from 'lucide-react';
import { useTranslation } from '@gs/i18n';
import {
  FEEDBACK_TYPES,
  TITLE_MAX,
  BODY_MAX,
  MAX_ATTACHMENTS,
  MAX_ATTACHMENT_BYTES,
  ATTACHMENT_CONTENT_TYPES,
  type FeedbackDiagnostics,
  type FeedbackType,
} from '../schemas';
import { useCreateTicket, uploadAttachment } from './hooks';
import { BTN_PRIMARY, BTN_SECONDARY, INPUT_BASE, TYPE_CHIP, CHIP_BASE } from './chips';

export interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
  /** Appelee apres creation reussie (id + numero) — ex: ouvrir le fil. */
  onCreated?: (ticket: { id: string; ticketNumber: string }) => void;
}

function captureDiagnostics(locale: string): FeedbackDiagnostics {
  return {
    userAgent: navigator.userAgent.slice(0, 400),
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    url: window.location.href.slice(0, 2000),
    locale,
    platform: navigator.platform?.slice(0, 100),
  };
}

export function FeedbackModal({ open, onClose, onCreated }: FeedbackModalProps) {
  const { t, i18n } = useTranslation();
  const [type, setType] = useState<FeedbackType>('GENERAL');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [includeDiag, setIncludeDiag] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const create = useCreateTicket();

  if (!open) return null;

  const pickType = (next: FeedbackType) => {
    setType(next);
    if (next === 'BUG') setIncludeDiag(true); // parite POC : bogue => diagnostics auto-coches
  };

  const onFilesPicked = (picked: FileList | null) => {
    if (!picked) return;
    setError(null);
    const next = [...files];
    for (const f of Array.from(picked)) {
      if (!ATTACHMENT_CONTENT_TYPES.test(f.type)) { setError(t('feedback:modal.attachmentTypeError')); return; }
      if (f.size > MAX_ATTACHMENT_BYTES) { setError(t('feedback:modal.attachmentSizeError')); return; }
      if (next.length >= MAX_ATTACHMENTS) { setError(t('feedback:modal.attachmentCountError', { max: MAX_ATTACHMENTS })); return; }
      next.push(f);
    }
    setFiles(next);
  };

  const reset = () => {
    setType('GENERAL'); setTitle(''); setDescription('');
    setIncludeDiag(false); setFiles([]); setError(null);
  };

  const submit = async () => {
    if (!title.trim() || !description.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await create.mutateAsync({
        type,
        title: title.trim(),
        description: description.trim(),
        diagnostics: includeDiag ? captureDiagnostics(i18n.language) : undefined,
      });
      // PJ apres creation (API : POST /:id/attachments) — echec PJ non bloquant.
      for (const f of files) {
        await uploadAttachment(created.id, f, 'ticket').catch(() => { /* fail-soft PJ */ });
      }
      reset();
      onClose();
      onCreated?.(created);
    } catch {
      setError(t('feedback:modal.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog" aria-modal="true" aria-label={t('feedback:modal.title')}
      onClick={() => !submitting && onClose()}>
      <div className="w-full max-w-lg rounded-xl bg-white shadow-xl dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-slate-800">
          <h2 className="text-body font-semibold text-slate-800 dark:text-slate-100">{t('feedback:modal.title')}</h2>
          <button type="button" onClick={onClose} disabled={submitting}
            aria-label={t('feedback:modal.cancel')}
            className="touch-manipulation rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800">
            <X size={15} />
          </button>
        </div>

        <div className="space-y-3.5 px-4 py-3.5">
          <div>
            <span className="mb-1 block text-meta font-medium text-slate-500 dark:text-slate-400">{t('feedback:modal.typeLabel')}</span>
            <div className="flex flex-wrap gap-1.5">
              {FEEDBACK_TYPES.map((ft) => (
                <button key={ft} type="button" onClick={() => pickType(ft)}
                  aria-pressed={type === ft}
                  className={[
                    'touch-manipulation rounded-lg px-2.5 py-1 text-meta font-semibold transition-colors',
                    type === ft
                      ? `${TYPE_CHIP[ft]} ring-1 ring-inset ring-slate-300 dark:ring-slate-600`
                      : 'bg-slate-50 text-slate-500 hover:bg-slate-100 dark:bg-slate-800/60 dark:text-slate-400 dark:hover:bg-slate-800',
                  ].join(' ')}>
                  {t(`feedback:type.${ft}`)}
                </button>
              ))}
            </div>
          </div>

          <label className="block">
            <span className="mb-1 block text-meta font-medium text-slate-500 dark:text-slate-400">{t('feedback:modal.titleLabel')}</span>
            <input className={INPUT_BASE} value={title} maxLength={TITLE_MAX}
              placeholder={t('feedback:modal.titlePlaceholder')}
              onChange={(e) => setTitle(e.target.value)} />
          </label>

          <label className="block">
            <span className="mb-1 block text-meta font-medium text-slate-500 dark:text-slate-400">{t('feedback:modal.descriptionLabel')}</span>
            <textarea className={`${INPUT_BASE} min-h-28 resize-y`} value={description}
              maxLength={BODY_MAX}
              placeholder={t('feedback:modal.descriptionPlaceholder')}
              onChange={(e) => setDescription(e.target.value)} />
            <span className="mt-0.5 block text-right text-chip text-slate-400">{description.length}/{BODY_MAX}</span>
          </label>

          <div>
            <span className="mb-1 block text-meta font-medium text-slate-500 dark:text-slate-400">{t('feedback:modal.attachments')}</span>
            {files.length > 0 && (
              <ul className="mb-1.5 space-y-1">
                {files.map((f, idx) => (
                  <li key={`${f.name}-${idx}`}
                    className="flex items-center justify-between rounded-md bg-slate-50 px-2.5 py-1.5 text-meta text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                    <span className="truncate">{f.name} · {Math.ceil(f.size / 1024)} Ko</span>
                    <button type="button" onClick={() => setFiles(files.filter((_, i) => i !== idx))}
                      aria-label={`${t('feedback:modal.cancel')} ${f.name}`}
                      className="touch-manipulation ml-2 shrink-0 rounded p-0.5 text-slate-400 hover:text-slate-600">
                      <X size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <input ref={fileInputRef} type="file" multiple accept="image/*,application/pdf"
              className="hidden" onChange={(e) => { onFilesPicked(e.target.files); e.target.value = ''; }} />
            <button type="button" onClick={() => fileInputRef.current?.click()}
              disabled={files.length >= MAX_ATTACHMENTS || submitting}
              className={`${BTN_SECONDARY} inline-flex items-center gap-1.5`}>
              <Paperclip size={13} /> {t('feedback:modal.addAttachment')}
            </button>
            <span className="ml-2 text-chip text-slate-400">{t('feedback:modal.attachmentHint', { max: MAX_ATTACHMENTS })}</span>
          </div>

          <label className="flex items-start gap-2 text-meta text-slate-600 dark:text-slate-300">
            <input type="checkbox" checked={includeDiag} onChange={(e) => setIncludeDiag(e.target.checked)}
              className="mt-0.5 accent-slate-700" />
            {t('feedback:modal.diagnostics')}
          </label>

          {error && (
            <p className="rounded-md bg-red-50 px-2.5 py-1.5 text-meta text-red-700 dark:bg-red-950/40 dark:text-red-300" role="alert">
              {error}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 px-4 py-3 dark:border-slate-800">
          <button type="button" className={`touch-manipulation ${BTN_SECONDARY} disabled:opacity-45`} onClick={onClose} disabled={submitting}>
            {t('feedback:modal.cancel')}
          </button>
          <button type="button" className={`touch-manipulation ${BTN_PRIMARY} disabled:opacity-45`} onClick={() => void submit()}
            disabled={submitting || !title.trim() || !description.trim()}>
            {submitting ? t('feedback:modal.submitting') : t('feedback:modal.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Chip type re-exporte pour la liste. */
export function TypeChip({ value }: { value: FeedbackType }) {
  const { t } = useTranslation();
  return <span className={`${CHIP_BASE} ${TYPE_CHIP[value]}`}>{t(`feedback:type.${value}`)}</span>;
}
